import React, { useState, useEffect } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import {
  EditOutlined,
  ExperimentOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import NumberWithUnit from '@/shared/components/NumberWithUnit'

const { Title, Text } = Typography

// ── Types ─────────────────────────────────────────────────────────────────────

interface CountStandard {
  id: string
  count_ne: number
  count_code: string
  yarn_type: 'CARDED' | 'COMBED' | 'COMPACT' | 'OE'
  standard_realization_pct: number
  blowroom_waste_pct: number
  card_waste_pct: number
  comber_noil_pct: number
  drawframe_waste_pct: number
  simplex_waste_pct: number
  ringframe_waste_pct: number
  winding_waste_pct: number
  waste_alert_tolerance_pct: number
  realization_critical_pct: number
  total_waste_pct?: number
  notes?: string
  is_active: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const YARN_TYPES = [
  { value: 'CARDED', label: 'Carded' },
  { value: 'COMBED', label: 'Combed' },
  { value: 'COMPACT', label: 'Compact' },
  { value: 'OE', label: 'OE (Open End)' },
]

function buildCountCode(ne: number, type: string): string {
  if (!ne || !type) return ''
  const prefix = type === 'OE' ? 'OE' : type === 'COMPACT' ? 'CM' : type === 'COMBED' ? 'CO' : 'CD'
  return `${prefix}${Math.round(ne)}s`
}

function calcTotalWaste(vals: Partial<CountStandard>): number {
  return (
    (vals.blowroom_waste_pct ?? 0) +
    (vals.card_waste_pct ?? 0) +
    (vals.comber_noil_pct ?? 0) +
    (vals.drawframe_waste_pct ?? 0) +
    (vals.simplex_waste_pct ?? 0) +
    (vals.ringframe_waste_pct ?? 0) +
    (vals.winding_waste_pct ?? 0)
  )
}

// ── Live Summary Banner ───────────────────────────────────────────────────────

function WasteSummaryBanner({ form }: { form: ReturnType<typeof Form.useForm>[0] }) {
  return (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldsValue }) => {
        const vals = getFieldsValue()
        const totalWaste = calcTotalWaste(vals)
        const realization = vals.standard_realization_pct ?? 0
        const impliedRealization = 100 - totalWaste
        const diff = Math.abs(realization - impliedRealization)
        const ok = diff < 0.5

        return (
          <div
            style={{
              padding: '10px 14px',
              background: ok ? '#f6ffed' : '#fff7e6',
              border: `1px solid ${ok ? '#b7eb8f' : '#ffd591'}`,
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            <Row gutter={24} align="middle">
              <Col>
                <Text strong>Total Waste: </Text>
                <Text style={{ color: '#cf1322', fontWeight: 700 }}>{totalWaste.toFixed(3)}%</Text>
              </Col>
              <Col>
                <Text strong>Implied Realization: </Text>
                <Text style={{ color: '#389e0d', fontWeight: 700 }}>{impliedRealization.toFixed(3)}%</Text>
              </Col>
              <Col>
                <Text strong>Set Realization: </Text>
                <Text style={{ color: '#1677ff', fontWeight: 700 }}>{realization.toFixed(3)}%</Text>
              </Col>
              {!ok && (
                <Col>
                  <Tag color="orange" icon={<InfoCircleOutlined />}>
                    Mismatch: {diff.toFixed(2)}%
                  </Tag>
                </Col>
              )}
            </Row>
          </div>
        )
      }}
    </Form.Item>
  )
}

// ── Form Drawer ───────────────────────────────────────────────────────────────

function CountStandardDrawer({
  open,
  onClose,
  editing,
  plantId,
}: {
  open: boolean
  onClose: () => void
  editing: CountStandard | null
  plantId: string
}) {
  const [form] = Form.useForm()
  const qc = useQueryClient()

  const createMut = useMutation({
    mutationFn: (d: any) => apiClient.post('/count-standards', d).then(r => r.data),
    onSuccess: (r: any) => {
      if (r?.approval_status === 'PENDING_APPROVAL') {
        message.info(r.message ?? 'Count standard sent for Plant Head approval')
      } else {
        message.success('Count standard created')
      }
      qc.invalidateQueries({ queryKey: ['count-standards'] })
      onClose()
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed to create'),
  })

  const updateMut = useMutation({
    mutationFn: (d: any) => apiClient.patch(`/count-standards/${editing!.id}`, d).then(r => r.data),
    onSuccess: (r: any) => {
      if (r?.approval_status === 'PENDING_APPROVAL') {
        message.info(r.message ?? 'Count standard change sent for Plant Head approval')
      } else {
        message.success('Count standard updated')
      }
      qc.invalidateQueries({ queryKey: ['count-standards'] })
      onClose()
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed to update'),
  })

  useEffect(() => {
    if (open) {
      if (editing) {
        form.setFieldsValue({ ...editing })
      } else {
        form.resetFields()
        form.setFieldsValue({
          blowroom_waste_pct: 2.0,
          card_waste_pct: 4.5,
          comber_noil_pct: 0,
          drawframe_waste_pct: 0.2,
          simplex_waste_pct: 0.1,
          ringframe_waste_pct: 1.5,
          winding_waste_pct: 0.5,
          waste_alert_tolerance_pct: 0.5,
          realization_critical_pct: 85.0,
          is_active: true,
        })
      }
    }
  }, [open, editing, form])

  const handleSubmit = () => {
    form.validateFields().then(vals => {
      const payload = { ...vals, plant_id: plantId }
      if (editing) updateMut.mutate(payload)
      else createMut.mutate(payload)
    })
  }

  const isPending = createMut.isPending || updateMut.isPending

  return (
    <Drawer
      title={
        <Space>
          <ExperimentOutlined />
          {editing ? `Edit — ${editing.count_code}` : 'New Count Standard'}
        </Space>
      }
      open={open}
      onClose={() => { onClose(); form.resetFields() }}
      width={760}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={() => { onClose(); form.resetFields() }}>Cancel</Button>
            <Button type="primary" loading={isPending} onClick={handleSubmit}>
              {editing ? 'Update Standard' : 'Create Standard'}
            </Button>
          </Space>
        </Row>
      }
    >
      <Form form={form} layout="vertical" size="small">
        {/* Count Identity */}
        <Card size="small" title="Count Identity" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item
                name="count_ne"
                label="Count (Ne)"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  max={120}
                  precision={1}
                  placeholder="e.g. 30"
                  onChange={() => {
                    const ne = form.getFieldValue('count_ne')
                    const type = form.getFieldValue('yarn_type')
                    if (ne && type) form.setFieldValue('count_code', buildCountCode(ne, type))
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="yarn_type"
                label="Yarn Type"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  options={YARN_TYPES}
                  placeholder="Select type"
                  onChange={type => {
                    const ne = form.getFieldValue('count_ne')
                    if (ne && type) form.setFieldValue('count_code', buildCountCode(ne, type))
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="count_code"
                label={
                  <Space size={4}>
                    Count Code
                    <Tooltip title="Auto-generated from Count + Type. Can be overridden.">
                      <InfoCircleOutlined style={{ color: '#aaa' }} />
                    </Tooltip>
                  </Space>
                }
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="e.g. CO30s" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Realization */}
        <Card size="small" title="Realization Targets" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="standard_realization_pct"
                label="Standard Realization %"
                rules={[{ required: true, message: 'Required' }]}
              >
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="realization_critical_pct" label="Critical Threshold %">
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="waste_alert_tolerance_pct" label="Alert Tolerance %">
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} max={10} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Waste per Department */}
        <Card size="small" title="Waste % per Department" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="blowroom_waste_pct" label="Blowroom">
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="card_waste_pct" label="Carding">
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="comber_noil_pct"
                label={
                  <Space size={4}>
                    Comber Noil
                    <Tooltip title="Set 0 for Carded/OE yarn where combing is not done">
                      <InfoCircleOutlined style={{ color: '#aaa' }} />
                    </Tooltip>
                  </Space>
                }
              >
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="drawframe_waste_pct" label="Drawframe">
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="simplex_waste_pct" label="Simplex">
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="ringframe_waste_pct" label="Ring Frame">
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="winding_waste_pct" label="Winding">
                <NumberWithUnit unit="%" style={{ width: '100%' }} precision={3} min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Live summary */}
        <WasteSummaryBanner form={form} />

        <Divider />
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="Optional notes about this standard" />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function CountStandardPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<CountStandard | null>(null)

  const { data: standards, isLoading } = useQuery<CountStandard[]>({
    queryKey: ['count-standards', plantId],
    queryFn: () =>
      apiClient
        .get('/count-standards', { params: { plant_id: plantId, active_only: false } })
        .then(r => r.data),
    enabled: !!plantId,
  })

  const list = Array.isArray(standards) ? standards : []

  const columns = [
    {
      title: 'Count (Ne)',
      dataIndex: 'count_ne',
      key: 'ne',
      width: 90,
      sorter: (a: CountStandard, b: CountStandard) => a.count_ne - b.count_ne,
      render: (v: number) => <Text strong>{v}s</Text>,
    },
    {
      title: 'Code',
      dataIndex: 'count_code',
      key: 'code',
      width: 90,
      render: (v: string) => <Tag color="blue" style={{ fontFamily: 'monospace' }}>{v}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: 'yarn_type',
      key: 'type',
      width: 90,
      render: (v: string) => (
        <Tag color={v === 'COMBED' ? 'purple' : v === 'COMPACT' ? 'cyan' : v === 'OE' ? 'geekblue' : 'default'}>
          {v}
        </Tag>
      ),
    },
    {
      title: 'Realization %',
      dataIndex: 'standard_realization_pct',
      key: 'real',
      width: 110,
      align: 'right' as const,
      render: (v: number) => (
        <Text strong style={{ color: '#389e0d' }}>{Number(v).toFixed(2)}%</Text>
      ),
    },
    {
      title: 'Total Waste %',
      key: 'twaste',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, r: CountStandard) => {
        const tw = r.total_waste_pct ?? calcTotalWaste(r)
        return <Text style={{ color: '#cf1322' }}>{Number(tw).toFixed(2)}%</Text>
      },
    },
    {
      title: 'Blowroom',
      dataIndex: 'blowroom_waste_pct',
      key: 'br',
      width: 85,
      align: 'right' as const,
      render: (v: number) => `${Number(v).toFixed(2)}%`,
    },
    {
      title: 'Card',
      dataIndex: 'card_waste_pct',
      key: 'card',
      width: 75,
      align: 'right' as const,
      render: (v: number) => `${Number(v).toFixed(2)}%`,
    },
    {
      title: 'Comber',
      dataIndex: 'comber_noil_pct',
      key: 'comb',
      width: 80,
      align: 'right' as const,
      render: (v: number) =>
        v > 0 ? `${Number(v).toFixed(2)}%` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Ring',
      dataIndex: 'ringframe_waste_pct',
      key: 'ring',
      width: 70,
      align: 'right' as const,
      render: (v: number) => `${Number(v).toFixed(2)}%`,
    },
    {
      title: 'Winding',
      dataIndex: 'winding_waste_pct',
      key: 'wind',
      width: 80,
      align: 'right' as const,
      render: (v: number) => `${Number(v).toFixed(2)}%`,
    },
    {
      title: 'Alert Tol',
      dataIndex: 'waste_alert_tolerance_pct',
      key: 'tol',
      width: 80,
      align: 'right' as const,
      render: (v: number) => `±${Number(v).toFixed(2)}%`,
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'active',
      width: 70,
      render: (v: boolean) => v ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: unknown, r: CountStandard) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => { setEditing(r); setDrawerOpen(true) }}
        >
          Edit
        </Button>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <SettingOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Count Standards
      </Title>
      <Text type="secondary">
        Define standard realization % and waste % per yarn count — used for production benchmarking
      </Text>

      {list.length === 0 && !isLoading && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message="No count standards defined yet. Add standards to enable production efficiency tracking."
        />
      )}

      <Card style={{ marginTop: 16 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Select
            value={plantId}
            onChange={setPlantId}
            style={{ width: 200 }}
            options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => { setEditing(null); setDrawerOpen(true) }}
          >
            + New Standard
          </Button>
        </Row>

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 30, showTotal: t => `${t} standards` }}
          scroll={{ x: 1200 }}
          rowClassName={r => !r.is_active ? 'ant-table-row-disabled' : ''}
        />
      </Card>

      <CountStandardDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null) }}
        editing={editing}
        plantId={plantId}
      />
    </div>
  )
}
